import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { 
  Send,
  MapPin,
  Phone,
  Mail,
  MessageCircle,
  Clock
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

const Contact = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/contact`, formData);
      toast.success(t('contact.form.success'));
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      toast.error('Failed to send message');
    }
    setLoading(false);
  };

  const contactInfo = [
    { icon: MapPin, label: 'Address', value: 'Dubai Internet City, Building 12, Dubai, UAE' },
    { icon: Phone, label: 'Phone', value: '+971 56 457 5525' },
    { icon: Mail, label: 'Email', value: 'afrigulfmarket@proton.me' },
    { icon: Clock, label: 'Hours', value: 'Sun-Thu: 9AM - 6PM (GST)' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="contact-page">
      {/* Header */}
      <div className="bg-[#0D4F8B] py-16">
        <div className="container-main text-center">
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('contact.title')}
          </h1>
          <p className="text-white/80 max-w-xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </div>

      <div className="container-main py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="card-base">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Send us a message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    {t('contact.form.name')} *
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    aria-label="Your name"
                    className="input-style"
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    {t('contact.form.email')} *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com"
                    aria-label="Your email"
                    className="input-style"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    {t('contact.form.message')} *
                  </label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="How can we help you?"
                    aria-label="Your message"
                    rows={5}
                    className="input-style"
                    data-testid="contact-message"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-[#0D4F8B] hover:bg-[#0A3D6B]"
                  disabled={loading}
                  data-testid="contact-submit"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? t('common.loading') : t('contact.form.submit')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="card-base">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t('contact.office')}
                </h2>
                <div className="space-y-6">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-[#0D4F8B]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-5 h-5 text-[#0D4F8B]" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{info.label}</p>
                        <p className="font-medium text-slate-900">{info.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* WhatsApp CTA */}
            <Card className="card-base bg-[#10B981] border-none">
              <CardContent className="p-8 text-center">
                <MessageCircle className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">
                  {t('contact.whatsapp')}
                </h3>
                <p className="text-white/80 mb-4">
                  Get instant support from our team
                </p>
                <a 
                  href="https://wa.me/971564575525"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-white text-[#10B981] hover:bg-white/90" data-testid="whatsapp-btn">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Chat Now
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Map Placeholder */}
            <Card className="card-base overflow-hidden">
              <div 
                className="h-64 bg-cover bg-center"
                style={{
                  backgroundImage: `url('https://images.unsplash.com/photo-1726533765275-a69cfd7f9897?w=800&q=80')`
                }}
              />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;


